import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Plus, FileText, Download, Eye, DollarSign } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { InvoiceCreator } from '@/components/admin/InvoiceCreator';

export default function InvoiceManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [pos, setPOs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);

  // Check if user is uber admin
  const isUberAdmin = user?.email === 'matt.monjan@infusedu.com' || user?.email === 'matt.monjan@infusesdu.com';

  useEffect(() => {
    if (user && isUberAdmin) {
      loadData();
    }
  }, [user, isUberAdmin]);

  const loadData = async () => {
    try {
      // Load all invoices
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select(`
          *,
          purchase_orders(po_number, bill_to_name),
          invoice_line_items(*)
        `)
        .order('created_at', { ascending: false });

      if (invoiceError) throw invoiceError;

      // Load POs without invoices
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select(`
          *,
          po_line_items(*)
        `)
        .eq('invoice_created', false)
        .order('created_at', { ascending: false });

      if (poError) throw poError;

      setInvoices(invoiceData || []);
      setPOs(poData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load invoice data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = (po: any) => {
    setSelectedPO(po);
    setShowCreateDialog(true);
  };

  const handleInvoiceCreated = () => {
    setShowCreateDialog(false);
    setSelectedPO(null);
    loadData();
    toast({
      title: "Success",
      description: "Invoice created successfully",
    });
  };

  if (!isUberAdmin) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access invoice management.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading invoice data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Invoice Management</h1>
          <p className="text-muted-foreground">
            Manage invoices and create new ones from purchase orders
          </p>
        </div>
      </div>

      {/* Pending POs Section */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Orders Pending Invoice</CardTitle>
          <CardDescription>
            {pos.length} purchase orders waiting for invoice creation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pos.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
              <p className="text-muted-foreground">
                No purchase orders are pending invoice creation.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {pos.map((po) => (
                <div key={po.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">PO #{po.po_number}</h4>
                    <p className="text-sm text-muted-foreground">
                      {po.bill_to_name} • ${po.total_amount.toFixed(2)} • {new Date(po.po_date).toLocaleDateString()}
                    </p>
                  </div>
                  <Button onClick={() => handleCreateInvoice(po)}>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Create Invoice
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing Invoices Section */}
      <Card>
        <CardHeader>
          <CardTitle>All Invoices</CardTitle>
          <CardDescription>
            {invoices.length} invoices created
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Invoices</h3>
              <p className="text-muted-foreground">
                No invoices have been created yet.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">Invoice #{invoice.invoice_number}</h4>
                      <Badge variant={
                        invoice.status === 'paid' ? 'default' : 
                        invoice.status === 'sent' ? 'secondary' : 'outline'
                      }>
                        {invoice.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      PO #{invoice.purchase_orders?.po_number} • {invoice.purchase_orders?.bill_to_name} • ${invoice.total_amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Created: {new Date(invoice.invoice_date).toLocaleDateString()} • Due: {new Date(invoice.due_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <InvoiceCreator po={selectedPO} onSuccess={handleInvoiceCreated} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
